import { useState, useMemo, useEffect, useRef } from "react";
import TextField from "@components/ui/textfields/TextField";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import { bookingsApi } from "@services/api/bookings";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import { searchDossiersFull, type ProjectHit } from "@services/algolia";
import { dossiersApi } from "@services/api/dossiers";
import { vehiclesApi } from "@services/api/vehicles";
import type { ClientVehicle } from "types/client_vehicle";
import { useDataStore } from "@store/user_org";
import { Switch } from "@components/ui/selection/Switch";
import { showNotification } from "context/notification.service";
import VehicleImage from "@components/ui/VehicleImage";

/** Minimal org-config shape this page needs (built from orgData below). */
interface OrgConfig {
  name: string;
  address?: string;
  postal?: string;
  city?: string;
  booking_min_days_ahead?: number;
  booking_max_days_ahead?: number;
  booking_sr_min_hours_ahead?: number;
  booking_safety_margin_hours?: number;
}

// ─── Types ──────────────────────────────────────────────────

export interface BookingData {
  immatriculation: string;
  km: string;
  service: string;
  intervention_type: string;
  intervention: string;
  /** Full list of selected intervention names (multi-select across categories). */
  interventions: string[];
  custom_intervention: string;
  dont_know_service: boolean;
  deposit_date: string | null;
  deposit_time: string;
  restitution_date: string | null;
  restitution_time: string;
  wait_on_site: boolean | null;
  mobility_option: string;
  phone: string;
  email: string;
  address: string;
  nom: string;
  prenom: string;
  privacy_accepted: boolean;
  /** Internal: how to notify the client — "whatsapp" | "email". */
  communication_channel: string;
  /** Internal: staff-chosen loaner vehicle id ("" = auto-assign). */
  rental_vehicle_id: string;
}

export const INITIAL: BookingData = {
  immatriculation: "",
  km: "",
  service: "",
  intervention_type: "",
  intervention: "",
  interventions: [],
  custom_intervention: "",
  dont_know_service: false,
  deposit_date: null,
  deposit_time: "",
  restitution_date: null,
  restitution_time: "",
  wait_on_site: null,
  mobility_option: "",
  phone: "",
  email: "",
  address: "",
  nom: "",
  prenom: "",
  privacy_accepted: false,
  communication_channel: "whatsapp",
  rental_vehicle_id: "",
};

const SERVICES = [
  { value: "mecanique", label: "Mécanique" },
  { value: "carrosserie", label: "Carrosserie" },
  { value: "vitrage", label: "Vitrage" },
  { value: "pneumatique", label: "Pneumatique" },
];

const MORNING_SLOTS = ["08:00", "08:15", "08:30", "08:45", "09:00", "09:15", "09:30", "09:45", "10:00", "10:15", "10:30", "10:45", "11:00"];
const AFTERNOON_SLOTS = ["14:00", "14:15", "14:30", "14:45", "15:00", "15:15", "15:30", "15:45", "16:00", "16:15", "16:30", "16:45", "17:00", "17:15", "17:30", "17:45", "18:00"];
export const ALL_SLOTS = [...MORNING_SLOTS, ...AFTERNOON_SLOTS];

const STEP_COUNT = 5; // service, vehicle, date, recap, contact (confirmation is separate)

export const NEED_CARDS = [
  { id: "entretien",          iconType: "entretien",          label: "Entretien & révision",  desc: "Vidange, filtres, contrôle général...",         service: "mecanique",   type: "entretien" },
  { id: "reparation",         iconType: "reparation",         label: "Réparation",            desc: "Freins, embrayage, distribution...",            service: "mecanique",   type: "reparation" },
  { id: "diagnostic",         iconType: "diagnostic",         label: "Diagnostic",            desc: "Recherche de panne, voyant...",                 service: "mecanique",   type: "diagnostic" },
  { id: "controle_technique", iconType: "controle_technique", label: "Contrôle technique",    desc: "CT, contre-visite...",                          service: "mecanique",   type: "controle_technique" },
  { id: "pneumatique",        iconType: "pneumatique",        label: "Pneumatiques",          desc: "Changement, équilibrage, réparation...",        service: "pneumatique", type: "entretien" },
  { id: "carrosserie",        iconType: "carrosserie",        label: "Carrosserie",           desc: "Carrosserie, échappement, amortisseurs...",     service: "carrosserie", type: "reparation" },
];

const MOBILITY_OPTIONS = [
  { key: "velo",     label: "Vélo électrique",       sub: "Gratuit" },
  { key: "pret",     label: "Véhicule de prêt",      sub: "Véhicule 15-20 ans, limité à 100 km" },
  { key: "location", label: "Véhicule de location",  sub: "Véhicule récent, km illimités — 25€/jour" },
  { key: "aucun",    label: "Aucun",                  sub: "" },
];

const SHORT_THRESHOLD = 150;

// ─── Utility functions ──────────────────────────────────────

function formatEstimatedDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < SHORT_THRESHOLD) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
  }
  const days = Math.ceil(minutes / 480);
  return `${days} jour${days > 1 ? "s" : ""}`;
}

function computeRestitutionTime(depositTime: string, totalMinutes: number): string {
  const [h, m] = depositTime.split(":").map(Number);
  const total = h * 60 + m + totalMinutes;
  const rh = Math.floor(total / 60);
  const rm = total % 60;
  if (rh >= 18) return "18:00";
  const rounded = Math.ceil(rm / 15) * 15;
  const fh = rounded >= 60 ? Math.min(rh + 1, 18) : rh;
  const fm = rounded >= 60 ? 0 : rounded;
  return `${String(fh).padStart(2, "0")}:${String(fm).padStart(2, "0")}`;
}

function addBusinessDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T12:00:00");
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) added++;
  }
  return toLocalDateStr(d);
}

/** Format a Date as YYYY-MM-DD in local timezone (avoids UTC shift from toISOString) */
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatSlot(slot: string): string {
  return slot.replace(":00", "h").replace(":15", "h15").replace(":30", "h30").replace(":45", "h45");
}

function formatDateFr(d: string | null): string {
  if (!d) return "--";
  const date = new Date(d + "T12:00:00");
  return date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function formatDateShort(d: string | null): string {
  if (!d) return "--";
  const parts = d.split("-");
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

// ─── Service icons ──────────────────────────────────────────

function ServiceIcon({ type, size = 26, color = "currentColor" }: { type: string; size?: number; color?: string }) {
  const s: React.CSSProperties = { width: size, height: size, flexShrink: 0 };
  const p = { stroke: color, strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, fill: "none" };
  if (type === "entretien") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>;
  if (type === "reparation") return <svg style={s} viewBox="0 0 24 24" {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
  if (type === "diagnostic") return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>;
  if (type === "controle_technique") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>;
  if (type === "pneumatique") return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /><line x1="12" y1="2" x2="12" y2="9" /><line x1="12" y1="15" x2="12" y2="22" /><line x1="2" y1="12" x2="9" y2="12" /><line x1="15" y1="12" x2="22" y2="12" /></svg>;
  if (type === "carrosserie") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M6 10l2.5-5h7L18 10" /><rect x="2" y="10" width="20" height="7" rx="2" /><circle cx="7" cy="17" r="2" fill={color} stroke="none" /><circle cx="17" cy="17" r="2" fill={color} stroke="none" /><line x1="12" y1="5" x2="12" y2="10" /></svg>;
  return <span style={s} />;
}

// ─── Chevron icon ───────────────────────────────────────────

function ChevronRight({ size = 16, color = "#A1A1A1" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
      <path d="M6 3l5 5-5 5" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Back arrow ─────────────────────────────────────────────

function BackArrow({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} aria-label="Retour" style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      width: 40, height: 40, borderRadius: "50%",
      background: "#F5F5F5", border: "1px solid #E5E5E5",
      cursor: "pointer", flexShrink: 0, padding: 0,
    }}>
      <svg width={20} height={20} viewBox="0 0 24 24" fill="none">
        <path d="M19 12H5M12 19l-7-7 7-7" stroke="#000" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

// ─── Progress dots ──────────────────────────────────────────

function ProgressDots({ step, total }: { step: number; total: number }) {
  return (
    <div style={{ display: "flex", gap: 6, justifyContent: "center", padding: "12px 0 4px" }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: i === step ? 24 : 8, height: 8, borderRadius: 4,
          background: i <= step ? "#000" : "#E5E5E5",
          transition: "all 0.3s ease",
        }} />
      ))}
    </div>
  );
}

// ─── Calendar ───────────────────────────────────────────────

const DAYS_HEADER = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

function MiniCalendar({
  selectedDate, onSelect, minDate, maxDate, fullDays, onMonthChange,
}: {
  selectedDate: string | null;
  onSelect: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  fullDays?: string[] | null;
  onMonthChange?: (monthStr: string) => void;
}) {
  const [viewMonth, setViewMonth] = useState(() => {
    const ref = selectedDate || minDate;
    if (ref) { const d = new Date(ref + "T12:00:00"); return { year: d.getFullYear(), month: d.getMonth() }; }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  useEffect(() => {
    onMonthChange?.(`${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, "0")}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-navigate to the selected date's month
  useEffect(() => {
    if (!selectedDate) return;
    const d = new Date(selectedDate + "T12:00:00");
    const m = { year: d.getFullYear(), month: d.getMonth() };
    if (m.year !== viewMonth.year || m.month !== viewMonth.month) {
      setViewMonth(m);
      onMonthChange?.(`${m.year}-${String(m.month + 1).padStart(2, "0")}`);
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeMonth = (next: { year: number; month: number }) => {
    setViewMonth(next);
    onMonthChange?.(`${next.year}-${String(next.month + 1).padStart(2, "0")}`);
  };

  const monthLabel = useMemo(() => {
    const d = new Date(viewMonth.year, viewMonth.month, 1);
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [viewMonth]);

  const weeks = useMemo(() => {
    const first = new Date(viewMonth.year, viewMonth.month, 1);
    const lastDay = new Date(viewMonth.year, viewMonth.month + 1, 0).getDate();
    let startDow = first.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;
    const cells: { day: number; inMonth: boolean; dateStr: string }[] = [];
    const prevMonthLast = new Date(viewMonth.year, viewMonth.month, 0).getDate();
    for (let i = startDow - 1; i >= 0; i--) {
      const d = prevMonthLast - i;
      const m = viewMonth.month === 0 ? 11 : viewMonth.month - 1;
      const y = viewMonth.month === 0 ? viewMonth.year - 1 : viewMonth.year;
      cells.push({ day: d, inMonth: false, dateStr: `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
    }
    for (let d = 1; d <= lastDay; d++) {
      cells.push({ day: d, inMonth: true, dateStr: `${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
    }
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        const m = viewMonth.month === 11 ? 0 : viewMonth.month + 1;
        const y = viewMonth.month === 11 ? viewMonth.year + 1 : viewMonth.year;
        cells.push({ day: d, inMonth: false, dateStr: `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
      }
    }
    const rows: typeof cells[] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [viewMonth]);

  const cutoff = minDate || toLocalDateStr(new Date());
  const loadingDays = fullDays === null || fullDays === undefined;
  const fullSet = useMemo(() => new Set(fullDays || []), [fullDays]);
  const todayStr = toLocalDateStr(new Date());

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0" }}>
        <button style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 8, background: "none", border: "none", cursor: "pointer" }}
          onClick={() => changeMonth(viewMonth.month === 0 ? { year: viewMonth.year - 1, month: 11 } : { year: viewMonth.year, month: viewMonth.month - 1 })}>
          <svg width={7} height={12}><path d="M6 1 L1 6 L6 11" fill="none" stroke="#9CA3AF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: "#374151", textTransform: "capitalize" }}>{monthLabel}</span>
        <button style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 8, background: "none", border: "none", cursor: "pointer" }}
          onClick={() => changeMonth(viewMonth.month === 11 ? { year: viewMonth.year + 1, month: 0 } : { year: viewMonth.year, month: viewMonth.month + 1 })}>
          <svg width={7} height={12}><path d="M1 1 L6 6 L1 11" fill="none" stroke="#9CA3AF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {DAYS_HEADER.map((d) => (
          <span key={d} style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 32, fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: "#9CA3AF" }}>{d}</span>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {weeks.map((week, wi) => (
          <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
            {week.map((cell) => {
              const isSelected = selectedDate === cell.dateStr;
              const isToday = cell.dateStr === todayStr && cell.inMonth;
              const dow = new Date(cell.dateStr + "T12:00:00").getDay();
              const isWeekend = dow === 0 || dow === 6;
              const beyondMax = maxDate ? cell.dateStr > maxDate : false;
              const full = cell.inMonth && cell.dateStr >= cutoff && fullSet.has(cell.dateStr);
              const disabled = !cell.inMonth || cell.dateStr < cutoff || beyondMax || full || loadingDays || isWeekend;
              return (
                <button key={cell.dateStr}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 38, height: 38, borderRadius: "50%", margin: "0 auto",
                    fontFamily: "Poppins", fontWeight: isSelected || isToday ? 600 : 400, fontSize: 14,
                    color: isSelected ? "#fff" : disabled ? "#D4D4D4" : isToday ? "#000" : "#4B5563",
                    background: isSelected ? "#000" : "transparent",
                    textDecoration: full ? "line-through" : "none",
                    cursor: disabled ? "default" : "pointer",
                    border: isToday && !isSelected ? "1.5px solid #D1D5DB" : "1.5px solid transparent",
                    transition: "background 0.15s",
                  }}
                  onClick={() => !disabled && onSelect(cell.dateStr)}
                  disabled={disabled}
                >{cell.day}</button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Step 0 — Service selection ─────────────────────────────

/** Resolve the category card a given intervention name belongs to (real or a "whole service" pseudo-entry). */
function cardForIntervention(name: string, interventions: Intervention[]): typeof NEED_CARDS[0] | undefined {
  const intv = interventions.find((i) => i.name === name);
  if (intv) return NEED_CARDS.find((c) => c.id === intv.type);
  return NEED_CARDS.find((c) => c.label === name);
}

export function StepService({
  data, update, interventions,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  interventions: Intervention[];
}) {
  const selected = useMemo(() => new Set(data.interventions), [data.interventions]);

  // Expand the category of the first selected intervention by default.
  const [openCard, setOpenCard] = useState<string | null>(() => {
    const first = data.interventions[0];
    return first ? cardForIntervention(first, interventions)?.id ?? null : null;
  });
  const [autreOpen, setAutreOpen] = useState(() => data.custom_intervention.trim().length > 0);

  // Keep the primary (first) selection in sync for downstream steps / payload.
  // Does NOT touch the "Autre" free text — both coexist.
  const applySelection = (names: string[]) => {
    const primaryCard = names.length ? cardForIntervention(names[0], interventions) : undefined;
    update("interventions", names);
    update("intervention", names[0] || "");
    update("intervention_type", primaryCard?.id || "");
    update("service", primaryCard?.service || "");
  };

  const toggleIntervention = (name: string) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name); else next.add(name);
    applySelection(Array.from(next));
  };

  const isAutre = autreOpen;

  const Check = () => (
    <div style={{ width: 18, height: 18, borderRadius: 5, background: "#000", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <svg width={11} height={11} viewBox="0 0 12 12" fill="none"><path d="M2.5 6l2.5 2.5L9.5 4" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" /></svg>
    </div>
  );
  const EmptyBox = () => (
    <div style={{ width: 18, height: 18, borderRadius: 5, border: "1.5px solid #D4D4D4", flexShrink: 0 }} />
  );

  const renderChip = (name: string, key: string) => {
    const isSel = selected.has(name);
    return (
      <button key={key} onClick={() => toggleIntervention(name)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          fontFamily: "Poppins", fontSize: 13, fontWeight: 500,
          padding: "9px 14px", borderRadius: 10,
          border: isSel ? "2px solid #000" : "1.5px solid #E5E5E5",
          background: isSel ? "#F5F5F5" : "#fff",
          color: isSel ? "#000" : "#444",
          cursor: "pointer",
        }}>
        {isSel ? <Check /> : <EmptyBox />}
        {name}
      </button>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h2 style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 22, color: "#000", margin: 0, lineHeight: "28px" }}>
          De quel service<br />avez-vous besoin ?
        </h2>
        <p style={{ fontFamily: "Poppins", fontSize: 14, color: "#A1A1A1", margin: 0 }}>
          Sélectionnez une ou plusieurs interventions.
        </p>
      </div>

      {/* Category accordion */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {NEED_CARDS.map((card) => {
          const matches = interventions.filter((i) => i.type === card.id && i.active);
          const open = openCard === card.id;
          const count = matches.filter((i) => selected.has(i.name)).length + (selected.has(card.label) ? 1 : 0);
          return (
            <div key={card.id} style={{ borderBottom: "1px solid #F3F4F6" }}>
              <button onClick={() => setOpenCard(open ? null : card.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", width: "100%",
                  background: "none", border: "none", cursor: "pointer", textAlign: "left",
                }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: count > 0 ? "#000" : "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <ServiceIcon type={card.iconType} size={20} color={count > 0 ? "#fff" : "#333"} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 14, color: "#000" }}>{card.label}</span>
                  <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>{card.desc}</span>
                </div>
                {count > 0 && (
                  <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: "#fff", background: "#000", borderRadius: 10, minWidth: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 6px" }}>{count}</span>
                )}
                <span style={{ display: "flex", transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                  <ChevronRight />
                </span>
              </button>
              {open && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "0 16px 16px" }}>
                  {matches.length > 0
                    ? matches.map((intv) => renderChip(intv.name, intv.id))
                    : renderChip(card.label, `whole-${card.id}`)}
                </div>
              )}
            </div>
          );
        })}

        {/* Autre / Je ne sais pas (free text) — coexiste avec les interventions */}
        <div style={{ borderBottom: "1px solid #F3F4F6" }}>
          <button onClick={() => setAutreOpen((o) => !o)}
            style={{
              display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", width: "100%",
              background: isAutre ? "#F5F5F5" : "none", border: "none",
              cursor: "pointer", textAlign: "left",
            }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: isAutre ? "#000" : "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={isAutre ? "#fff" : "#333"} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 14, color: "#000" }}>Autre / Je ne sais pas</span>
              <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>Décrivez le besoin (ajouté comme intervention)</span>
            </div>
            <span style={{ display: "flex", transform: isAutre ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
              <ChevronRight />
            </span>
          </button>
          {isAutre && (
            <textarea
              value={data.custom_intervention}
              onChange={(e) => update("custom_intervention", e.target.value)}
              placeholder="Décrivez votre besoin..."
              rows={3}
              autoFocus
              style={{
                fontFamily: "Poppins", fontSize: 13, color: "#333", width: "100%", boxSizing: "border-box",
                border: "1.5px solid #E5E5E5", borderRadius: 10, padding: "10px 12px",
                outline: "none", resize: "none", background: "#FAFAFA", margin: "0 0 16px",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Step 1 — Vehicle (plate input) ─────────────────────────

function StepVehicle({
  data, update, orgId,
  clientQuery, setClientQuery, clientVehicles, setClientVehicles, addingVehicle, setAddingVehicle,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  orgId: string;
  clientQuery: string;
  setClientQuery: (v: string) => void;
  clientVehicles: ClientVehicle[];
  setClientVehicles: (v: ClientVehicle[]) => void;
  addingVehicle: boolean;
  setAddingVehicle: (v: boolean) => void;
}) {
  const [lookup, setLookup] = useState<{ found: boolean; marque?: string; modele?: string; image_url?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Internal: search a client OR a plate (Algolia covers nom / téléphone / immat).
  const [clientResults, setClientResults] = useState<ProjectHit[]>([]);
  const clientTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Immat autocomplete (Algolia vehicles) shown under the plate input.
  type PlateHit = { plate: string; marque?: string; modele?: string; dossier_id?: string; client_name?: string };
  const [plateResults, setPlateResults] = useState<PlateHit[]>([]);
  const plateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchClients = (query: string) => {
    setClientQuery(query);
    if (clientTimerRef.current) clearTimeout(clientTimerRef.current);
    if (!orgId || query.trim().length < 2) { setClientResults([]); return; }
    clientTimerRef.current = setTimeout(async () => {
      try { setClientResults(await searchDossiersFull(query, orgId)); }
      catch { setClientResults([]); }
    }, 300);
  };

  const searchPlates = (raw: string) => {
    if (plateTimerRef.current) clearTimeout(plateTimerRef.current);
    const q = raw.replace(/[\s-]/g, "");
    if (!orgId || q.length < 2) { setPlateResults([]); return; }
    plateTimerRef.current = setTimeout(async () => {
      try { setPlateResults(await bookingsApi.searchVehicles(q, orgId)); }
      catch { setPlateResults([]); }
    }, 300);
  };

  // Two INDEPENDENT flows, no cross-triggering:
  //  - client-first (selectClient): prefill client + show its vehicles picker
  //  - immat-first (selectPlate): set the chosen vehicle + link its client,
  //    WITHOUT opening the vehicle picker (the vehicle is already chosen).

  const _prefillContact = (dossier: { contacts?: { is_primary?: boolean; nom?: string; prenom?: string; phone?: string; email?: string }[]; phone?: string; email?: string }) => {
    const contact = dossier.contacts?.find((c) => c.is_primary) || dossier.contacts?.[0];
    if (contact?.nom) update("nom", contact.nom);
    update("prenom", contact?.prenom || "");
    const phone = contact?.phone || dossier.phone;
    if (phone) update("phone", phone);
    const email = contact?.email || dossier.email;
    if (email) update("email", email);
  };

  // Client-first: pick a client → propose its vehicles.
  const selectClient = async (hit: ProjectHit) => {
    setClientResults([]);
    setClientQuery(hit.label);
    try {
      const dossier = await dossiersApi.get(hit.value);
      const vehicles = (dossier.vehicles || []).filter((v) => v.plate);
      setClientVehicles(vehicles);
      if (vehicles.length === 1) { update("immatriculation", vehicles[0].plate); setAddingVehicle(false); }
      else if (vehicles.length === 0) { update("immatriculation", ""); setAddingVehicle(true); }
      else { update("immatriculation", ""); setAddingVehicle(false); }  // show the picker
      _prefillContact(dossier);
    } catch { /* staff can fill manually */ }
  };

  // Immat-first: the vehicle is chosen — just link its client. No picker.
  const selectPlate = async (hit: PlateHit) => {
    setPlateResults([]);
    update("immatriculation", hit.plate);
    setClientVehicles([]);     // keep the chosen vehicle — don't open the picker
    setAddingVehicle(true);
    if (hit.client_name && !data.nom) setClientQuery(hit.client_name);
    if (hit.dossier_id) {
      try { _prefillContact(await dossiersApi.get(hit.dossier_id)); }
      catch { /* staff can fill manually */ }
    }
  };

  // Reset both the vehicle and the linked client (e.g. start over).
  const changeOwner = () => {
    setClientVehicles([]);
    setClientQuery("");
    setAddingVehicle(true);
    update("immatriculation", "");
    update("nom", ""); update("prenom", ""); update("phone", ""); update("email", "");
  };

  // Immat-first: keep the chosen plate but unlink the client (changement de
  // propriétaire) so staff can search the new owner via the top search box.
  const clearLinkedClient = () => {
    setClientQuery("");
    update("nom", ""); update("prenom", ""); update("phone", ""); update("email", "");
  };

  useEffect(() => {
    const plate = data.immatriculation.replace(/[\s-]/g, "");
    if (plate.length < 7) { setLookup(null); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await bookingsApi.lookupVehicle(data.immatriculation, orgId);
        setLookup(result);
      } catch { setLookup(null); }
      setLoading(false);
    }, 400);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [data.immatriculation, orgId]);

  const showPicker = clientVehicles.length > 0 && !addingVehicle;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, flex: 1 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h2 style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 22, color: "#000", margin: 0 }}>
          Quel est votre véhicule ?
        </h2>
        <p style={{ fontFamily: "Poppins", fontSize: 14, color: "#A1A1A1", margin: 0 }}>
          Recherchez le client ou l'immatriculation, ou saisissez la plaque.
        </p>
      </div>

      {/* Internal: client / plate search */}
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={clientQuery}
          onChange={(e) => searchClients(e.target.value)}
          placeholder="Rechercher un client (nom, téléphone, immatriculation)…"
          style={{
            fontFamily: "Poppins", fontSize: 14, color: "#000", width: "100%",
            border: "1.5px solid #E5E5E5", borderRadius: 12, padding: "10px 14px",
            outline: "none", background: "#fff",
          }}
        />
        {clientResults.length > 0 && (
          <div style={{ position: "absolute", left: 0, right: 0, top: "100%", zIndex: 30, marginTop: 4, background: "#fff", border: "1px solid #E5E5E5", borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.10)", maxHeight: 240, overflowY: "auto" }}>
            {clientResults.map((hit) => (
              <button key={hit.value} type="button" onClick={() => selectClient(hit)}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: "transparent", border: "none", cursor: "pointer" }}>
                <div style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#000" }}>{hit.label}</div>
                {(hit.extra_label || hit.extra_label_2) && (
                  <div style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>{[hit.extra_label, hit.extra_label_2].filter(Boolean).join(" · ")}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Vehicle picker (client's vehicles) */}
      {showPicker && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>Véhicule du client</span>
          {clientVehicles.map((v) => {
            const selected = v.plate.replace(/[\s-]/g, "").toUpperCase() === data.immatriculation.replace(/[\s-]/g, "").toUpperCase();
            return (
              <button key={v.id || v.plate} type="button" onClick={() => update("immatriculation", v.plate)}
                style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 12, width: "100%", textAlign: "left", padding: "12px 14px", borderRadius: 12, background: selected ? "#EEF2FF" : "#FAFAFA", border: `2px solid ${selected ? "#6366F1" : "#E5E5E5"}`, cursor: "pointer" }}>
                <div style={{ height: 44, flexShrink: 0, display: "flex", alignItems: "center" }}>
                  <VehicleImage marque={v.marque} modele={v.modele} coloris={v.coloris} mec={v.mec} className="h-[44px]" iconSize={22} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: "#000" }}>{[v.marque, v.modele].filter(Boolean).join(" ") || "Véhicule"}</span>
                  <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>{v.plate}</span>
                </div>
              </button>
            );
          })}
          <button type="button" onClick={() => { update("immatriculation", ""); setAddingVehicle(true); }}
            style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: "1.5px dashed #D4D4D4", background: "transparent", fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#6366F1", cursor: "pointer" }}>
            + Ajouter un véhicule
          </button>
          <button type="button" onClick={changeOwner}
            style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1", textDecoration: "underline", cursor: "pointer" }}>
            Le véhicule a changé de propriétaire
          </button>
        </div>
      )}

      {/* Plate input (manual / add-vehicle path) */}
      <div style={{ display: showPicker ? "none" : "flex", flexDirection: "column", gap: 12 }}>
        {clientVehicles.length > 0 && (
          <button type="button" onClick={() => { update("immatriculation", ""); setAddingVehicle(false); }}
            style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, fontFamily: "Poppins", fontSize: 13, color: "#6366F1", cursor: "pointer" }}>
            ← Choisir un véhicule du client
          </button>
        )}
        {/* Immat-first: client linked from the chosen plate */}
        {clientVehicles.length === 0 && data.nom && data.immatriculation && (
          <div className="flex flex-row items-center gap-3 p-2.5 rounded-lg" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#15803D" }}>Client lié</span>
              <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#166534" }}>{[data.prenom, data.nom].filter(Boolean).join(" ")}</span>
            </div>
            <button type="button" onClick={clearLinkedClient}
              style={{ background: "none", border: "none", padding: 0, fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1", textDecoration: "underline", cursor: "pointer" }}>
              Changement de propriétaire
            </button>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, position: "relative" }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#666" }}>Immatriculation</span>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, padding: "14px 16px",
            border: "2px solid #E5E5E5", borderRadius: 14, background: "#FAFAFA",
          }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="22" height="18" rx="3" />
              <line x1="1" y1="9" x2="23" y2="9" />
            </svg>
            <input
              type="text"
              value={data.immatriculation}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 7);
                let result = "";
                for (let i = 0; i < raw.length; i++) {
                  if (i === 2 || i === 5) result += "-";
                  result += raw[i];
                }
                update("immatriculation", result);
                searchPlates(raw);
              }}
              placeholder="AB-123-CD"
              autoFocus
              style={{
                fontFamily: "Poppins", fontWeight: 600, fontSize: 20, letterSpacing: 2,
                color: "#000", border: "none", outline: "none", background: "transparent",
                flex: 1, minWidth: 0, width: "100%",
              }}
            />
          </div>
          {/* Immat autocomplete (Algolia) — pick a plate to prefill its linked client */}
          {plateResults.length > 0 && (
            <div style={{ position: "absolute", left: 0, right: 0, top: "100%", zIndex: 30, marginTop: 4, background: "#fff", border: "1px solid #E5E5E5", borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.10)", maxHeight: 240, overflowY: "auto" }}>
              {plateResults.map((hit, i) => (
                <button key={`${hit.plate}-${i}`} type="button" onClick={() => selectPlate(hit)}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: "transparent", border: "none", cursor: "pointer" }}>
                  <div style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#000" }}>
                    {hit.plate}{hit.client_name ? ` — ${hit.client_name}` : ""}
                  </div>
                  {(hit.marque || hit.modele) && (
                    <div style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>{[hit.marque, hit.modele].filter(Boolean).join(" ")}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Vehicle result */}
        <div style={{ minHeight: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid #E5E5E5", borderTopColor: "#000", animation: "spin 0.6s linear infinite" }} />
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>Identification...</span>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
          {!loading && lookup?.found && (
            <div style={{
              display: "flex", alignItems: "center", gap: 14, width: "100%",
              padding: "14px 16px", borderRadius: 14, background: "#F5F5F5", border: "1px solid #E5E5E5",
            }}>
              {lookup.image_url && <img src={lookup.image_url} alt="" style={{ height: 52, objectFit: "contain" }} />}
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 15, color: "#000" }}>
                  {lookup.marque} {lookup.modele}
                </span>
                <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>
                  {data.immatriculation}
                </span>
              </div>
            </div>
          )}
          {!loading && lookup && !lookup.found && data.immatriculation.replace(/[\s-]/g, "").length >= 7 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "12px 14px", borderRadius: 12, background: "#FFF7ED", border: "1px solid #FED7AA" }}>
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#EA580C" }}>
                Véhicule non reconnu -- vous pouvez continuer
              </span>
            </div>
          )}
        </div>

        {/* Mileage */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#666" }}>Kilométrage <span style={{ color: "#A1A1A1", fontWeight: 400 }}>(facultatif)</span></span>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, padding: "14px 16px",
            border: "2px solid #E5E5E5", borderRadius: 14, background: "#FAFAFA",
          }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 12l4-2.5" /><path d="M12 7v1M7 12h1M17 12h-1" />
            </svg>
            <input
              type="text"
              inputMode="numeric"
              value={data.km}
              onChange={(e) => {
                const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 7);
                update("km", digits ? Number(digits).toLocaleString("fr-FR") : "");
              }}
              placeholder="Ex : 85 000"
              style={{
                fontFamily: "Poppins", fontWeight: 600, fontSize: 18,
                color: "#000", border: "none", outline: "none", background: "transparent",
                flex: 1, minWidth: 0, width: "100%",
              }}
            />
            <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#A1A1A1", flexShrink: 0 }}>km</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 2 — Date & Time ───────────────────────────────────

export function StepDateTime({
  data, update, orgId, minDaysAhead, maxDaysAhead, isFast, srMinHoursAhead, estimatedMinutes, onEstimatedReadyTimes, urgent,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  orgId: string;
  minDaysAhead: number;
  maxDaysAhead: number;
  isFast: boolean;
  srMinHoursAhead: number;
  estimatedMinutes: number;
  onEstimatedReadyTimes?: (times: Record<string, string>) => void;
  /** Manager urgency override → bypass atelier capacity in availability. */
  urgent?: boolean;
}) {
  const [fullDays, setFullDays] = useState<string[] | null>(null);
  const [unavailable, setUnavailable] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  // True while an auto-selected date is still being validated (its real availability not yet confirmed).
  // We never surface a selected date to the user until we know it actually has open slots.
  const [autoValidating, setAutoValidating] = useState(false);

  const { minDate, cutoffSlots } = useMemo(() => {
    if (isFast) {
      const now = new Date();
      const cutoff = new Date(now.getTime() + srMinHoursAhead * 60 * 60 * 1000);
      const cutoffDate = toLocalDateStr(cutoff);
      const cutoffMinutes = cutoff.getHours() * 60 + cutoff.getMinutes();
      const disabled = ALL_SLOTS.filter((s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m < cutoffMinutes; });
      return { minDate: cutoffDate, cutoffSlots: disabled };
    }
    const d = new Date(); d.setDate(d.getDate() + minDaysAhead);
    return { minDate: toLocalDateStr(d), cutoffSlots: [] as string[] };
  }, [minDaysAhead, isFast, srMinHoursAhead]);

  const maxDate = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + maxDaysAhead);
    return toLocalDateStr(d);
  }, [maxDaysAhead]);

  const currentMonthStr = useMemo(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }, []);
  const viewedMonthRef = useRef(currentMonthStr);
  const fetchFullDays = (month: string) => {
    if (!orgId) return;
    viewedMonthRef.current = month;
    bookingsApi.getFullDays(month, orgId, isFast || undefined, data.service || undefined, estimatedMinutes || undefined, urgent || undefined)
      .then((res) => setFullDays((prev) => [...(prev || []).filter((d) => !d.startsWith(month)), ...res.full_days]))
      .catch(() => setFullDays((prev) => prev ?? []));
  };

  // Fetch full days on mount / when params change. Only clear date when params actually change.
  const lastFetchKeyRef = useRef("");
  useEffect(() => {
    const key = `${orgId}_${isFast}_${data.service}_${estimatedMinutes}`;
    const changed = key !== lastFetchKeyRef.current;
    lastFetchKeyRef.current = key;
    if (changed) {
      setFullDays(null); update("deposit_date", ""); update("deposit_time", "");
    }
    fetchFullDays(currentMonthStr);
    const viewedMonth = viewedMonthRef.current;
    if (viewedMonth !== currentMonthStr) fetchFullDays(viewedMonth);
  }, [orgId, isFast, data.service, estimatedMinutes, urgent]);

  // Auto-select first non-full weekday when no date is chosen yet.
  // The picked date stays "validating" (hidden from the UI as selected) until the availability
  // call below confirms it has real open slots — so a date is never shown selected then struck out.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (data.deposit_date || fullDays === null) return;
    const fullSet = new Set(fullDays);
    const d = new Date(minDate + "T12:00:00");
    const max = new Date(maxDate + "T12:00:00");
    for (let i = 0; i < 60; i++) {
      if (d > max) break;
      const ds = toLocalDateStr(d);
      if (d.getDay() !== 0 && d.getDay() !== 6 && !fullSet.has(ds)) {
        autoSelectedRef.current = true;
        setAutoValidating(true);
        update("deposit_date", ds);
        return;
      }
      d.setDate(d.getDate() + 1);
    }
    // No candidate day found — stop showing the "searching" state.
    setAutoValidating(false);
  }, [fullDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch availability when a date is selected (auto or manual)
  useEffect(() => {
    if (!data.deposit_date || !orgId) { setUnavailable([]); return; }
    setLoadingSlots(true);
    const selectedDate = data.deposit_date;
    const wasAutoSelected = autoSelectedRef.current;
    autoSelectedRef.current = false;
    bookingsApi.getAvailability(selectedDate, data.service || "Mécanique", orgId, isFast || undefined, estimatedMinutes || undefined, urgent || undefined)
      .then((res) => {
        if (selectedDate !== data.deposit_date) return;
        const allSlots = ALL_SLOTS.filter((s) => !new Set(res.unavailable_slots).has(s));
        // If auto-selected and no slots available, mark as full and let the auto-select effect advance.
        // Keep autoValidating true so the next candidate is also vetted before being shown.
        if (wasAutoSelected && allSlots.length === 0) {
          setFullDays((prev) => [...(prev || []), selectedDate]);
          update("deposit_date", "");
          return;
        }
        setUnavailable(res.unavailable_slots);
        onEstimatedReadyTimes?.(res.estimated_ready_times || {});
        if (allSlots.length > 0 && (!data.deposit_time || !allSlots.includes(data.deposit_time))) {
          update("deposit_time", allSlots[0]);
        }
        // Confirmed: the date now has real availability and can be revealed as selected.
        setAutoValidating(false);
      })
      .catch(() => { setUnavailable([]); setAutoValidating(false); })
      .finally(() => setLoadingSlots(false));
  }, [data.deposit_date, data.service, orgId, isFast, urgent]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableSlots = useMemo(() => {
    const allUnavailable = isFast && data.deposit_date === minDate
      ? new Set([...unavailable, ...cutoffSlots])
      : new Set(unavailable);
    return {
      morning: MORNING_SLOTS.filter((s) => !allUnavailable.has(s)),
      afternoon: AFTERNOON_SLOTS.filter((s) => !allUnavailable.has(s)),
    };
  }, [unavailable, cutoffSlots, isFast, data.deposit_date, minDate]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h2 style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 22, color: "#000", margin: 0 }}>
          Choisir une date et une heure
        </h2>
        {estimatedMinutes > 0 && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 8, background: "#F5F5F5",
            alignSelf: "flex-start", marginTop: 4,
          }}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#666" }}>
              Durée estimée : {formatEstimatedDuration(estimatedMinutes)}
            </span>
          </div>
        )}
      </div>

      {fullDays === null ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "32px 0" }}>
          <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid #E5E5E5", borderTopColor: "#000", animation: "spin 0.7s linear infinite" }} />
          <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>Chargement des disponibilités...</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#333" }}>Sélectionnez disponibilité</span>
        <MiniCalendar
          selectedDate={autoValidating ? null : data.deposit_date}
          onSelect={(d) => { setAutoValidating(false); autoSelectedRef.current = false; update("deposit_date", d); update("deposit_time", ""); }}
          minDate={minDate}
          maxDate={maxDate}
          fullDays={fullDays}
          onMonthChange={fetchFullDays}
        />
      </div>
      )}

      {/* While auto-selecting, don't reveal any date/slot until availability is confirmed */}
      {autoValidating && fullDays !== null && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 16 }}>
          <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid #E5E5E5", borderTopColor: "#000", animation: "spin 0.6s linear infinite" }} />
          <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>Recherche du premier créneau disponible…</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Time slots — grid (table) layout, grouped by morning / afternoon */}
      {data.deposit_date && !loadingSlots && !autoValidating && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {([["Matin", availableSlots.morning], ["Après-midi", availableSlots.afternoon]] as const).map(([label, slots]) => (
            slots.length > 0 && (
              <div key={label} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#666" }}>{label}</span>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: 8 }}>
                  {slots.map((slot) => {
                    const isSelected = data.deposit_time === slot;
                    return (
                      <button key={slot} onClick={() => update("deposit_time", slot)}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "center",
                          padding: "11px 6px", borderRadius: 10,
                          border: isSelected ? "2px solid #000" : "1.5px solid #E5E5E5",
                          background: isSelected ? "#000" : "#fff",
                          cursor: "pointer", fontFamily: "Poppins", fontSize: 14, fontWeight: 500,
                          color: isSelected ? "#fff" : "#000",
                        }}>
                        {formatSlot(slot)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )
          ))}
          {availableSlots.morning.length === 0 && availableSlots.afternoon.length === 0 && (
            <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#EF4444", textAlign: "center", padding: 16 }}>Aucun créneau disponible ce jour</span>
          )}
        </div>
      )}
      {data.deposit_date && loadingSlots && !autoValidating && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ width: 20, height: 20, borderRadius: "50%", border: "2px solid #E5E5E5", borderTopColor: "#000", animation: "spin 0.6s linear infinite" }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </div>
  );
}

// ─── Step 3 — Recap ─────────────────────────────────────────

function StepRecap({
  data, update, estimatedMinutes, safetyMarginHours, isDiagnostic, restitutionMarginDays, estimatedReadyTimes, orgConfig,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  estimatedMinutes: number;
  safetyMarginHours: number;
  isDiagnostic: boolean;
  restitutionMarginDays: number;
  estimatedReadyTimes: Record<string, string>;
  orgConfig: OrgConfig;
}) {
  const orgId = useDataStore((s) => s.orgData)?.id ?? "";
  // Internal: list available loaner/rental vehicles so staff can pick one.
  const _USAGE: Record<string, string> = { pret: "pret", location: "location", velo: "velos" };
  const loanerUsage = _USAGE[data.mobility_option];
  // Fetch every fleet vehicle free over the whole loan window (deposit → restitution) once,
  // then derive availability per usage so we can grey out mobility options that have no free
  // vehicle (rather than only telling staff after they've already picked the option).
  const [availVehicles, setAvailVehicles] = useState<{ id: string; name?: string; plate?: string; marque?: string; modele?: string; usage?: string }[]>([]);
  const [availLoaded, setAvailLoaded] = useState(false);
  useEffect(() => {
    if (!orgId || !data.deposit_date) { setAvailVehicles([]); setAvailLoaded(false); return; }
    let cancelled = false;
    setAvailLoaded(false);
    vehiclesApi.available({
      org_id: orgId,
      start_date: data.deposit_date,
      end_date: data.restitution_date || data.deposit_date,
    })
      .then((vs) => { if (!cancelled) setAvailVehicles(vs as never); })
      .catch(() => { if (!cancelled) setAvailVehicles([]); })
      .finally(() => { if (!cancelled) setAvailLoaded(true); });
    return () => { cancelled = true; };
  }, [orgId, data.deposit_date, data.restitution_date]);

  const loaners = useMemo(
    () => (loanerUsage ? availVehicles.filter((v) => v.usage === loanerUsage) : []),
    [availVehicles, loanerUsage],
  );
  // Only flag an option as unavailable once availability for the current dates has actually
  // loaded — otherwise we'd briefly grey everything out while the fetch is in flight.
  const mobilityUnavailable = useMemo<Record<string, boolean>>(
    () => ({
      pret: !!data.deposit_date && availLoaded && !availVehicles.some((v) => v.usage === "pret"),
      location: !!data.deposit_date && availLoaded && !availVehicles.some((v) => v.usage === "location"),
    }),
    [availVehicles, availLoaded, data.deposit_date],
  );

  // Diagnostic always requires leaving the vehicle — no "wait on site" option
  const isShort = !isDiagnostic && estimatedMinutes > 0 && estimatedMinutes < SHORT_THRESHOLD;
  const totalMinutes = estimatedMinutes + safetyMarginHours * 60;
  // Number of business days the vehicle must stay. Driven by the configurable per-intervention
  // delay (min_days_margin, e.g. 2 days for a diagnostic), the estimated duration, and a 1-day
  // floor for diagnostics.
  const durationDays = estimatedMinutes > 0 ? Math.max(1, Math.ceil(estimatedMinutes / 480)) : 0;
  const immobilizationDays = Math.max(restitutionMarginDays, durationDays, isDiagnostic ? 1 : 0, 1);
  const longMinDate = data.deposit_date
    ? addBusinessDays(data.deposit_date, immobilizationDays)
    : undefined;

  const getReadyTime = (time: string) => {
    if (estimatedReadyTimes[time]) return estimatedReadyTimes[time];
    return computeRestitutionTime(time, totalMinutes);
  };

  // Auto-compute restitution for "wait on site"
  useEffect(() => {
    if (!isShort || data.wait_on_site !== true || !data.deposit_date || !data.deposit_time) return;
    const restTime = getReadyTime(data.deposit_time);
    update("restitution_date", data.deposit_date);
    update("restitution_time", restTime);
    update("mobility_option", "attente");
  }, [data.wait_on_site, data.deposit_date, data.deposit_time, isShort, totalMinutes, estimatedReadyTimes]);

  // Default the restitution for long interventions to the earliest allowed date; the client can
  // still pick a later date in the calendar below.
  useEffect(() => {
    if (isShort || !data.deposit_date || !longMinDate) return;
    if (isDiagnostic && data.wait_on_site !== false) update("wait_on_site", false);
    if (!data.restitution_date || data.restitution_date < longMinDate) {
      update("restitution_date", longMinDate);
      if (!data.restitution_time) update("restitution_time", "18:00");
    }
    if (!data.mobility_option) update("mobility_option", "location");
  }, [data.deposit_date, isShort, isDiagnostic, longMinDate]);

  const earliestPickup = useMemo(() => {
    if (!data.deposit_time) return "18:00";
    return getReadyTime(data.deposit_time);
  }, [data.deposit_time, totalMinutes, estimatedReadyTimes]);

  const pickupSlots = useMemo(() => ALL_SLOTS.filter((s) => s >= earliestPickup), [earliestPickup]);
  // For "Non, je repasse" the client picks the pickup date (defaults to same day, but can be later).
  const pickupSameDay = !data.restitution_date || data.restitution_date === data.deposit_date;
  const pickupTimeSlots = pickupSameDay ? pickupSlots : ALL_SLOTS;

  useEffect(() => {
    if (!isShort || data.wait_on_site !== false || !data.deposit_date) return;
    // Default the pickup date to the drop-off day; never let it fall before the drop-off.
    if (!data.restitution_date || data.restitution_date < data.deposit_date) update("restitution_date", data.deposit_date);
    if (!data.mobility_option || data.mobility_option === "attente") update("mobility_option", "velo");
    // Same-day pickup can't be earlier than the ready time; a later day allows any slot.
    if (pickupSameDay) {
      if (!data.restitution_time || data.restitution_time < earliestPickup) update("restitution_time", pickupSlots[0] || "18:00");
    } else if (!data.restitution_time) {
      update("restitution_time", ALL_SLOTS[0]);
    }
  }, [data.wait_on_site, data.mobility_option, data.deposit_date, data.restitution_date, isShort, earliestPickup, pickupSameDay]);

  const selectedCard = NEED_CARDS.find((c) => c.id === data.intervention_type);
  const serviceLabel = data.dont_know_service
    ? "À déterminer avec le garage"
    : (selectedCard?.label || data.service || "Autre");
  const interventionLabel = data.interventions.length > 0
    ? data.interventions.join(", ")
    : data.custom_intervention || "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h2 style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 22, color: "#000", margin: 0 }}>
          Vérifiez les détails de votre réservation
        </h2>
      </div>

      {/* Summary cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Service */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#666" }}>Service</span>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, border: "1px solid #E5E5E5" }}>
            {selectedCard && <ServiceIcon type={selectedCard.iconType} size={20} color="#000" />}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 14, color: "#000" }}>{serviceLabel}</span>
              {interventionLabel && <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>{interventionLabel}</span>}
              {estimatedMinutes > 0 && <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>Durée estimée : {formatEstimatedDuration(estimatedMinutes)}</span>}
            </div>
          </div>
        </div>

        {/* Vehicle */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#666" }}>Véhicule</span>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, border: "1px solid #E5E5E5" }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="22" height="18" rx="3" /><line x1="1" y1="9" x2="23" y2="9" />
            </svg>
            <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: "#000", letterSpacing: 1 }}>{data.immatriculation}</span>
          </div>
        </div>

        {/* Date & time */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#666" }}>Date & heure</span>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, border: "1px solid #E5E5E5" }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="3" /><path d="M3 10h18M8 2v4M16 2v4" />
            </svg>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 14, color: "#000", textTransform: "capitalize" }}>{formatDateFr(data.deposit_date)}</span>
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A1A1A1" }}>{data.deposit_time ? formatSlot(data.deposit_time) : ""}</span>
            </div>
          </div>
        </div>

        {/* Garage */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#666" }}>Garage</span>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, border: "1px solid #E5E5E5" }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 14, color: "#000" }}>{orgConfig.name}</span>
              {orgConfig.address && <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>{orgConfig.address}{orgConfig.postal || orgConfig.city ? `, ${orgConfig.postal} ${orgConfig.city}` : ""}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Mobility / Restitution section */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: "1px solid #F3F4F6", paddingTop: 16 }}>
        {/* SHORT: wait on site question */}
        {isShort && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#666" }}>
              Souhaitez-vous attendre sur place ?
            </span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {([true, false] as const).map((val) => {
                const selected = data.wait_on_site === val;
                return (
                  <button key={String(val)} onClick={() => { update("wait_on_site", val); if (val) update("mobility_option", "attente"); }}
                    style={{
                      fontFamily: "Poppins", fontWeight: 500, fontSize: 14,
                      padding: "12px 16px", borderRadius: 12,
                      border: selected ? "2px solid #000" : "1.5px solid #E5E5E5",
                      background: selected ? "#F5F5F5" : "#fff",
                      color: selected ? "#000" : "#666",
                      cursor: "pointer",
                    }}>
                    {val ? "Oui, j'attends" : "Non, je repasse"}
                  </button>
                );
              })}
            </div>
            {data.wait_on_site === true && data.deposit_date && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
                <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#166534" }}>
                  Votre véhicule sera prêt vers {getReadyTime(data.deposit_time).replace(":", "h")}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Mobility options: shown when not waiting */}
        {((isShort && data.wait_on_site === false) || !isShort) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#666" }}>Solution de mobilité</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {MOBILITY_OPTIONS.map((opt) => {
                const isSelected = data.mobility_option === opt.key;
                const unavailable = !!mobilityUnavailable[opt.key];
                return (
                  <button key={opt.key} disabled={unavailable}
                    onClick={() => { if (!unavailable) update("mobility_option", opt.key); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                      borderRadius: 12, fontFamily: "Poppins",
                      border: isSelected ? "2px solid #000" : "1.5px solid #E5E5E5",
                      background: unavailable ? "#F9F9F9" : isSelected ? "#F5F5F5" : "#fff",
                      cursor: unavailable ? "not-allowed" : "pointer",
                      opacity: unavailable ? 0.5 : 1,
                    }}>
                    <span style={{ fontWeight: 500, fontSize: 14, color: isSelected ? "#000" : "#444" }}>{opt.label}</span>
                    {opt.sub && <span style={{ fontSize: 12, color: "#999" }}>{opt.sub}</span>}
                    {unavailable && <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: "#DC2626" }}>Indisponible</span>}
                  </button>
                );
              })}
            </div>

            {/* Internal: choose the loaner vehicle (client gets auto-assigned) */}
            {loanerUsage && data.deposit_date && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#666" }}>Véhicule à attribuer</span>
                <button onClick={() => update("rental_vehicle_id", "")}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, fontFamily: "Poppins",
                    border: !data.rental_vehicle_id ? "2px solid #000" : "1.5px solid #E5E5E5",
                    background: !data.rental_vehicle_id ? "#F5F5F5" : "#fff", cursor: "pointer", textAlign: "left",
                  }}>
                  <span style={{ fontWeight: 500, fontSize: 13, color: "#000" }}>Automatique (premier disponible)</span>
                </button>
                {loaners.map((v) => {
                  const sel = data.rental_vehicle_id === v.id;
                  const label = [v.name || [v.marque, v.modele].filter(Boolean).join(" "), v.plate].filter(Boolean).join(" · ") || "Véhicule";
                  return (
                    <button key={v.id} onClick={() => update("rental_vehicle_id", v.id)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, fontFamily: "Poppins",
                        border: sel ? "2px solid #000" : "1.5px solid #E5E5E5",
                        background: sel ? "#F5F5F5" : "#fff", cursor: "pointer", textAlign: "left",
                      }}>
                      <span style={{ fontWeight: 500, fontSize: 13, color: sel ? "#000" : "#444" }}>{label}</span>
                    </button>
                  );
                })}
                {loaners.length === 0 && (
                  <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#EA580C" }}>Aucun véhicule libre sur le créneau — sera à attribuer manuellement.</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Short + not waiting: pickup date + time */}
        {isShort && data.wait_on_site === false && data.mobility_option && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#333" }}>Date de récupération</span>
              <MiniCalendar
                selectedDate={data.restitution_date}
                onSelect={(d) => { update("restitution_date", d); update("restitution_time", ""); }}
                minDate={data.deposit_date || undefined}
                fullDays={[]}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#666" }}>
                Heure de récupération{pickupSameDay ? ` (à partir de ${earliestPickup.replace(":", "h")})` : ""}
              </span>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: 8 }}>
                {pickupTimeSlots.map((slot) => {
                  const isSelected = data.restitution_time === slot;
                  return (
                    <button key={slot} onClick={() => update("restitution_time", slot)}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: "Poppins", fontWeight: 500, fontSize: 14,
                        padding: "11px 6px", borderRadius: 10,
                        border: isSelected ? "2px solid #000" : "1.5px solid #E5E5E5",
                        background: isSelected ? "#000" : "#fff",
                        color: isSelected ? "#fff" : "#000",
                        cursor: "pointer",
                      }}>
                      {formatSlot(slot)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Long / diagnostic: always let the client choose a restitution date */}
        {!isShort && data.deposit_date && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: "#333" }}>
              Date de récupération souhaitée
            </span>
            {(isDiagnostic || immobilizationDays >= 2) && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#FFF7ED", border: "1px solid #FED7AA" }}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#9A3412" }}>
                  Le véhicule nécessite une immobilisation de {immobilizationDays} jour{immobilizationDays > 1 ? "s" : ""}{isDiagnostic ? " pour réaliser le diagnostic" : ""}. La récupération est possible à partir du {formatDateShort(longMinDate || null)}.
                </span>
              </div>
            )}
            <MiniCalendar
              selectedDate={data.restitution_date}
              onSelect={(d) => { update("restitution_date", d); update("restitution_time", ""); }}
              minDate={longMinDate}
              fullDays={[]}
            />
            {data.restitution_date && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: 8 }}>
                {ALL_SLOTS.map((slot) => {
                  const isSelected = data.restitution_time === slot;
                  return (
                    <button key={slot} onClick={() => update("restitution_time", slot)}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: "Poppins", fontWeight: 500, fontSize: 14,
                        padding: "11px 6px", borderRadius: 10,
                        border: isSelected ? "2px solid #000" : "1.5px solid #E5E5E5",
                        background: isSelected ? "#000" : "#fff",
                        color: isSelected ? "#fff" : "#000",
                        cursor: "pointer",
                      }}>
                      {formatSlot(slot)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 4 — Contact ───────────────────────────────────────

function StepContact({
  data, update, orgId,
}: {
  data: BookingData;
  update: (k: keyof BookingData, v: unknown) => void;
  orgId: string;
}) {
  const [clientLookup, setClientLookup] = useState<{ found: boolean; nom?: string; prenom?: string; email?: string } | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const phone = data.phone.replace(/\s/g, "");
    if (phone.length < 10) { setClientLookup(null); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLookingUp(true);
      try {
        const result = await bookingsApi.lookupClient(data.phone, orgId);
        setClientLookup(result);
        if (result.found) {
          if (result.nom) update("nom", result.nom);
          if (result.prenom) update("prenom", result.prenom);
          if (result.email) update("email", result.email);
        }
      } catch { setClientLookup(null); }
      setLookingUp(false);
    }, 400);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [data.phone, orgId]);

  const recognized = clientLookup?.found === true;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h2 style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 22, color: "#000", margin: 0 }}>
          Vos coordonnées
        </h2>
        <p style={{ fontFamily: "Poppins", fontSize: 14, color: "#A1A1A1", margin: 0 }}>
          Ces informations sont utilisées uniquement pour votre réservation.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <TextField label="Téléphone" value={data.phone} onChange={(v) => update("phone", v.replace(/^\+33\s?/, "0"))} placeholder="06 12 34 56 78" type="phone" />
        {lookingUp && <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1", paddingLeft: 4 }}>Recherche...</span>}
        {recognized && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span style={{ fontSize: 13, color: "#166534", fontFamily: "Poppins" }}>Rebonjour ! Vos infos ont été pré-remplies.</span>
          </div>
        )}

        {!recognized && (
          <>
            <TextField label="Email" value={data.email} onChange={(v) => update("email", v)} placeholder="votre@email.com" type="email" />
            <TextField label="Nom" value={data.nom} onChange={(v) => update("nom", v)} />
            <TextField label="Prénom" value={data.prenom} onChange={(v) => update("prenom", v)} />
          </>
        )}

        {/* Adresse — demandée en même temps que les coordonnées */}
        <TextField label="Adresse" value={data.address} onChange={(v) => update("address", v)} placeholder="N° et rue, code postal, ville" />

        {/* Notification channel (internal) — WhatsApp or Email */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#666" }}>Notifier le client par</span>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {([["whatsapp", "WhatsApp"], ["email", "Email"]] as const).map(([val, lbl]) => {
              const selected = (data.communication_channel || "whatsapp") === val;
              return (
                <button key={val} type="button" onClick={() => update("communication_channel", val)}
                  style={{
                    fontFamily: "Poppins", fontWeight: 500, fontSize: 13, padding: "10px 12px", borderRadius: 12, cursor: "pointer",
                    border: selected ? "2px solid #000" : "1.5px solid #E5E5E5",
                    background: selected ? "#F5F5F5" : "#fff", color: selected ? "#000" : "#666",
                  }}>
                  {lbl}
                </button>
              );
            })}
          </div>
          {data.communication_channel === "email" && recognized && (
            <TextField label="Email" value={data.email} onChange={(v) => update("email", v)} placeholder="client@email.com" type="email" />
          )}
          {data.communication_channel === "email" && !data.email && (
            <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#EA580C" }}>Renseignez un email pour notifier le client par email.</span>
          )}
        </div>

      </div>
    </div>
  );
}

// ─── Confirmation ───────────────────────────────────────────

function buildCalendarUrl(data: BookingData, orgName: string): string {
  if (!data.deposit_date || !data.deposit_time) return "";
  const [h, m] = data.deposit_time.split(":").map(Number);
  const start = new Date(`${data.deposit_date}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`);
  const end = new Date(start.getTime() + 60 * 60 * 1000); // 1h block
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d+/, "");
  const title = encodeURIComponent(`RDV ${orgName} - ${data.immatriculation}`);
  const details = encodeURIComponent(`Rendez-vous garage ${orgName}\nVéhicule : ${data.immatriculation}`);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}`;
}

function ConfirmationScreen({ data, bookingId, trackingUrl, whatsappPhone, orgConfig, internalMode, onClose }: {
  data: BookingData; bookingId: string; trackingUrl: string; whatsappPhone: string; orgConfig: OrgConfig; internalMode?: boolean; onClose?: () => void;
}) {
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const ref = bookingId ? `#${bookingId.slice(0, 8).toUpperCase()}` : "";
  const depositLabel = data.deposit_date ? `${formatDateShort(data.deposit_date)} à ${data.deposit_time}` : "";
  const waMessage = `Bonjour, je confirme mon RDV ${ref} du ${depositLabel} pour mon véhicule ${data.immatriculation || ""}. Merci !`;
  const waLink = whatsappPhone
    ? `https://wa.me/${whatsappPhone.replace(/[^0-9+]/g, "")}?text=${encodeURIComponent(waMessage)}`
    : null;
  const hasEmail = !!data.email;
  const selectedCard = NEED_CARDS.find((c) => c.id === data.intervention_type);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24, flex: 1, paddingTop: 20 }}>
      {/* Big checkmark */}
      <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#F0FDF4", border: "3px solid #BBF7D0", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width={40} height={40} viewBox="0 0 24 24" fill="none">
          <path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
        <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 22, color: "#000" }}>
          {internalMode ? "RDV créé !" : "Réservation confirmée !"}
        </span>
        <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#A1A1A1", textAlign: "center" }}>
          {internalMode ? "Le client a été notifié." : "Nous avons hâte de vous accueillir."}
        </span>
      </div>

      {/* Details */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F3F4F6" }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="3" /><path d="M3 10h18M8 2v4M16 2v4" />
          </svg>
          <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#000", textTransform: "capitalize" }}>
            {formatDateFr(data.deposit_date)} à {data.deposit_time ? formatSlot(data.deposit_time) : ""}
          </span>
        </div>
        {selectedCard && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F3F4F6" }}>
            <ServiceIcon type={selectedCard.iconType} size={18} color="#666" />
            <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#000" }}>{selectedCard.label}</span>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F3F4F6" }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="22" height="18" rx="3" /><line x1="1" y1="9" x2="23" y2="9" />
          </svg>
          <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#000" }}>{data.immatriculation}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F3F4F6" }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
          </svg>
          <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#000" }}>{orgConfig.name}</span>
        </div>
      </div>

      {/* Internal: notification status + close */}
      {internalMode && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
          {data.communication_channel !== "email" && data.phone && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#166534" }}>Confirmation WhatsApp envoyée au client ({data.phone})</span>
            </div>
          )}
          {data.communication_channel === "email" && data.email && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#EEF2FF", border: "1px solid #C7D2FE" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" /></svg>
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#4338CA" }}>Email de confirmation envoyé ({data.email})</span>
            </div>
          )}
          <button onClick={onClose}
            style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 15, padding: "14px 20px", borderRadius: 12, background: "#000", color: "#fff", border: "none", cursor: "pointer", width: "100%", marginTop: 4 }}>
            Fermer
          </button>
        </div>
      )}

      {/* WhatsApp / Email confirm (client-facing) */}
      {!internalMode && (!emailConfirmed ? (
        <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: 8 }}>
          {waLink && (
            <a href={waLink} target="_blank" rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                width: "100%", padding: "14px 20px", background: "#25D366", color: "#fff",
                fontFamily: "Poppins", borderRadius: 12, textDecoration: "none", cursor: "pointer",
              }}>
              <svg width="22" height="22" viewBox="0 0 32 32" fill="white" style={{ flexShrink: 0 }}>
                <path fillRule="evenodd" clipRule="evenodd" d="M16 3C8.82 3 3 8.82 3 16c0 2.49.7 4.81 1.91 6.78L3 29l6.42-1.83A12.94 12.94 0 0016 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm0 23.5c-2.17 0-4.2-.62-5.93-1.7l-.42-.26-3.82 1.09 1.02-3.72-.28-.44A10.44 10.44 0 015.5 16C5.5 10.2 10.2 5.5 16 5.5S26.5 10.2 26.5 16 21.8 26.5 16 26.5zm5.95-7.8c-.33-.16-1.93-.95-2.23-1.06-.3-.11-.52-.16-.73.16-.22.33-.84 1.06-1.03 1.28-.19.22-.38.24-.71.08-.33-.16-1.38-.51-2.63-1.62-.97-.87-1.63-1.93-1.82-2.26-.19-.33-.02-.5.14-.67.15-.14.33-.38.49-.57.16-.19.22-.33.33-.55.11-.22.05-.41-.03-.57-.08-.16-.73-1.77-1-2.42-.27-.64-.54-.55-.73-.56h-.63c-.22 0-.57.08-.87.41-.3.33-1.14 1.11-1.14 2.72 0 1.6 1.17 3.15 1.33 3.37.16.22 2.3 3.51 5.57 4.92.78.34 1.39.54 1.86.69.78.25 1.49.21 2.05.13.63-.09 1.93-.79 2.2-1.55.28-.76.28-1.41.19-1.55-.08-.14-.3-.22-.63-.38z" />
              </svg>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Confirmer via WhatsApp</span>
                <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.85 }}>Discutez en direct avec le garage</span>
              </div>
            </a>
          )}
          {hasEmail && (
            <button onClick={() => { setEmailConfirmed(true); }}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                width: "100%", padding: "14px 20px", background: "#fff", color: "#374151",
                fontFamily: "Poppins", fontSize: 14, fontWeight: 600, borderRadius: 12,
                border: "1.5px solid #E5E5E5", cursor: "pointer",
              }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" />
              </svg>
              Confirmer par email
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", borderRadius: 10, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#16A34A" }}>Email de confirmation envoyé à {data.email}</span>
          </div>
          {trackingUrl && (
            <a href={trackingUrl} target="_blank" rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: "100%", padding: "14px 20px", background: "#5C6BFF", color: "#fff",
                fontFamily: "Poppins", fontSize: 14, fontWeight: 600, borderRadius: 12,
                textDecoration: "none", cursor: "pointer",
              }}>
              Suivre ma demande
            </a>
          )}
        </div>
      ))}

      {/* SMS reminder note (client-facing) */}
      {!internalMode && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: "#F9FAFB" }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#6B7280" }}>
            Vous recevrez un SMS de rappel avant votre rendez-vous.
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Main Full Page Component ───────────────────────────────

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
  /** Internal staff mode (relaxed margins, client/immat search). Always true here. */
  internalMode?: boolean;
  /** Manager → may force a full slot (urgent / atelier override). */
  isManager?: boolean;
}

export default function InternalBookingPage({ onClose, onSuccess, internalMode = true, isManager }: Props) {
  const orgData = useDataStore((s) => s.orgData);
  const orgId = orgData?.id ?? "";
  const orgConfig: OrgConfig = {
    name: orgData?.infos?.name ?? "",
    address: orgData?.infos?.address,
    postal: orgData?.infos?.postal,
    city: orgData?.infos?.city,
    booking_min_days_ahead: orgData?.config?.booking_min_days_ahead,
    booking_max_days_ahead: orgData?.config?.booking_max_days_ahead,
    booking_sr_min_hours_ahead: orgData?.config?.booking_sr_min_hours_ahead,
    booking_safety_margin_hours: orgData?.config?.booking_safety_margin_hours,
  };
  // Internal staff bypass the public booking delays.
  const minDaysAhead = internalMode ? 0 : (orgConfig.booking_min_days_ahead ?? 1);
  const maxDaysAhead = internalMode ? 365 : (orgConfig.booking_max_days_ahead ?? 90);
  const srMinHoursAhead = internalMode ? 0 : (orgConfig.booking_sr_min_hours_ahead ?? 2);
  const safetyMarginHours = orgConfig.booking_safety_margin_hours ?? 1;

  const [step, setStep] = useState(0);
  const [data, setData] = useState<BookingData>({ ...INITIAL });
  const [confirmed, setConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [bookingId, setBookingId] = useState<string>("");
  const [estimatedReadyTimes, setEstimatedReadyTimes] = useState<Record<string, string>>({});
  const [trackingUrl, setTrackingUrl] = useState<string>("");
  const [whatsappPhone, setWhatsappPhone] = useState<string>("");
  // Manager force / atelier-urgency override (sends force:true + urgent availability).
  const [forceSlot, setForceSlot] = useState(false);
  const [urgentMode, setUrgentMode] = useState(false);
  // Internal client/immat selection — lifted so it survives step navigation.
  const [clientQuery, setClientQuery] = useState("");
  const [clientVehicles, setClientVehicles] = useState<ClientVehicle[]>([]);
  const [addingVehicle, setAddingVehicle] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    interventionsApi.list(orgId).then(setInterventions).catch(() => {});
  }, [orgId]);

  const update = (key: keyof BookingData, value: unknown) => setData((prev) => ({ ...prev, [key]: value }));

  // Multiple interventions (possibly across categories) can be selected.
  const selectedIntvs = useMemo(() => interventions.filter((i) => data.interventions.includes(i.name)), [interventions, data.interventions]);
  // "Fast" / express only apply when EVERY selected intervention qualifies (otherwise the slow workshop drives the flow).
  const isFast = selectedIntvs.length > 0 && selectedIntvs.every((i) => i.fast);
  const isExpress = selectedIntvs.length > 0 && selectedIntvs.every((i) => i.express_24h);
  const isDiagnostic = selectedIntvs.some((i) => i.type === "diagnostic") || data.intervention_type === "diagnostic";
  // Express interventions (e.g. carrosserie EAD) are bookable as soon as the next day
  const effectiveMinDaysAhead = isExpress ? Math.min(minDaysAhead, 1) : minDaysAhead;
  // Total estimated duration is the sum of the selected interventions (matches the backend).
  const estimatedMinutes = selectedIntvs.reduce((sum, i) => sum + (i.estimated_minutes || 0), 0);
  // Configurable per-intervention immobilization delay (days); max across the selection.
  const restitutionMarginDays = selectedIntvs.reduce((max, i) => Math.max(max, i.min_days_margin || 0), 0);

  // Reasons the current step can't be validated — drives the button AND is shown
  // to staff so a disabled "Confirmer" is never a mystery. Prénom is NOT required
  // (hidden once the client is recognised; pros have none).
  const blockingReasons = (() => {
    const r: string[] = [];
    if (step === 0) {
      if (!(data.interventions.length > 0 || data.custom_intervention.trim().length > 0))
        r.push("Choisissez au moins une intervention");
    } else if (step === 1) {
      if (data.immatriculation.replace(/[\s-]/g, "").length < 7) r.push("Renseignez l'immatriculation du véhicule");
    } else if (step === 2) {
      if (!data.deposit_date) r.push("Choisissez une date de dépôt");
      if (!data.deposit_time) r.push("Choisissez une heure de dépôt");
    } else if (step === 3) {
      const isShort = !isDiagnostic && estimatedMinutes > 0 && estimatedMinutes < SHORT_THRESHOLD;
      if (isShort && data.wait_on_site === null) {
        r.push("Indiquez si le client attend sur place");
      } else if (isShort && data.wait_on_site) {
        if (!data.restitution_date) r.push("Date de restitution manquante");
      } else {
        if (!data.mobility_option) r.push("Choisissez une solution de mobilité");
        if (!data.restitution_date) r.push("Choisissez une date de restitution");
        if (!data.restitution_time) r.push("Choisissez une heure de récupération");
      }
    } else if (step === 4) {
      if (data.phone.replace(/\s/g, "").length < 10) r.push("Téléphone valide requis (10 chiffres)");
      if (!data.nom) r.push("Nom du client requis");
      if (data.communication_channel === "email" && !data.email) r.push("Email requis pour notifier par email");
    }
    return r;
  })();
  const canProceed = blockingReasons.length === 0;

  const handleNext = async () => {
    if (step < STEP_COUNT - 1) {
      setStep(step + 1);
    } else {
      setSubmitting(true);
      setSubmitError(false);
      try {
        const result = await bookingsApi.create({
          org_id: orgId,
          plate: data.immatriculation,
          km: data.km ? parseInt(data.km.replace(/[^0-9]/g, ""), 10) || 0 : 0,
          service_type: SERVICES.find((s) => s.value === data.service)?.label || data.service || "Non précisé",
          intervention_type: data.intervention_type || "",
          interventions: data.interventions,
          custom_intervention: data.custom_intervention || undefined,
          is_fast: isFast,
          express_24h: isExpress,
          mobility_option: data.mobility_option || "",
          wait_on_site: data.wait_on_site ?? false,
          tire_option: "",
          deposit_date: data.deposit_date || "",
          deposit_time: data.deposit_time,
          restitution_date: data.restitution_date || "",
          restitution_time: data.restitution_time || "",
          nom: data.nom,
          prenom: data.prenom,
          phone: data.phone.replace(/\s/g, "").replace(/^\+33/, "0"),
          email: data.email || undefined,
          address: data.address || undefined,
          communication_channel: data.communication_channel || "whatsapp",
          rental_vehicle_id: data.rental_vehicle_id || undefined,
          internal: true,
          force: forceSlot,
        });
        setBookingId(result.id);
        setTrackingUrl((result as { tracking_url?: string }).tracking_url || "");
        setWhatsappPhone(result.whatsapp_phone || "");
        setConfirmed(true);
        // Always show a success toast — some mounts close the modal in onSuccess
        // (e.g. QuickAppointmentModal), so the confirmation screen alone isn't enough.
        if (internalMode) showNotification({ type: "success", message: "RDV créé" });
        onSuccess?.();
      } catch (e) {
        console.error("Error submitting booking:", e);
        if (internalMode) showNotification({ type: "error", message: "Erreur lors de la création du RDV" });
        setSubmitError(true);
      }
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const stepTitles = ["Choix du service", "Véhicule", "Date & heure", "Récapitulatif", "Coordonnées"];

  // ─── Layout ───────────────────────────────────────────────

  return (
    <div style={{
      maxHeight: "90vh", display: "flex", flexDirection: "column",
      fontFamily: "Poppins", background: "#fff", borderRadius: 16,
      width: 480, maxWidth: "95vw", overflow: "hidden", position: "relative",
    }}>
      {/* Header — sticky so the back arrow stays visible on scroll */}
      {!confirmed && (
        <div style={{ position: "sticky", top: 0, zIndex: 20, display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #F3F4F6", background: "#fff" }}>
          {step > 0 && <BackArrow onClick={handleBack} />}
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 16, color: "#000", flex: 1, textAlign: step > 0 ? "center" : "left" }}>
            {step === 0 ? "Nouveau RDV client" : stepTitles[step]}
          </span>
          <button onClick={onClose} aria-label="Fermer" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, lineHeight: 0 }}>
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none"><path d="M2 2l12 12M14 2L2 14" stroke="#A1A1A1" strokeWidth={2} strokeLinecap="round" /></svg>
          </button>
        </div>
      )}
      {confirmed && (
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 12px 0" }}>
          <button onClick={onClose} aria-label="Fermer" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, lineHeight: 0 }}>
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none"><path d="M2 2l12 12M14 2L2 14" stroke="#A1A1A1" strokeWidth={2} strokeLinecap="round" /></svg>
          </button>
        </div>
      )}

      {/* Content */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        padding: "20px 20px 0", overflowY: "auto",
        maxWidth: 480, width: "100%", margin: "0 auto",
      }}>
        {confirmed ? (
          <ConfirmationScreen data={data} bookingId={bookingId} trackingUrl={trackingUrl} whatsappPhone={whatsappPhone} orgConfig={orgConfig} internalMode={internalMode} onClose={onClose} />
        ) : submitError ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, flex: 1, justifyContent: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width={28} height={28} viewBox="0 0 16 16" fill="none">
                <path d="M2 2l12 12M14 2L2 14" stroke="#EF4444" strokeWidth={2} strokeLinecap="round" />
              </svg>
            </div>
            <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 18, color: "#000" }}>Erreur</span>
            <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#A1A1A1", textAlign: "center" }}>Une erreur est survenue. Veuillez réessayer.</span>
            <button onClick={() => setSubmitError(false)}
              style={{
                fontFamily: "Poppins", fontWeight: 600, fontSize: 14,
                padding: "14px 32px", borderRadius: 12, background: "#000",
                color: "#fff", border: "none", cursor: "pointer",
              }}>
              Réessayer
            </button>
          </div>
        ) : (
          <>
            {step === 0 && <StepService data={data} update={update} interventions={interventions} />}
            {step === 1 && <StepVehicle data={data} update={update} orgId={orgId} clientQuery={clientQuery} setClientQuery={setClientQuery} clientVehicles={clientVehicles} setClientVehicles={setClientVehicles} addingVehicle={addingVehicle} setAddingVehicle={setAddingVehicle} />}
            {step === 2 && <StepDateTime data={data} update={update} orgId={orgId} minDaysAhead={effectiveMinDaysAhead} maxDaysAhead={maxDaysAhead} isFast={isFast} srMinHoursAhead={srMinHoursAhead} estimatedMinutes={estimatedMinutes} onEstimatedReadyTimes={setEstimatedReadyTimes} urgent={urgentMode} />}
            {step === 3 && <StepRecap data={data} update={update} estimatedMinutes={estimatedMinutes} safetyMarginHours={safetyMarginHours} isDiagnostic={isDiagnostic} restitutionMarginDays={restitutionMarginDays} estimatedReadyTimes={estimatedReadyTimes} orgConfig={orgConfig} />}
            {step === 4 && <StepContact data={data} update={update} orgId={orgId} />}
          </>
        )}
      </div>

      {/* Footer */}
      {!confirmed && !submitError && (
        <div style={{
          padding: "12px 20px 20px",
          maxWidth: 480, width: "100%", margin: "0 auto",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          {/* Manager-only: force a full slot (bypass atelier capacity) */}
          {isManager && (step === 2 || step === 3) && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, background: forceSlot ? "#FEF2F2" : "#FAFAFA", border: `1px solid ${forceSlot ? "#FECACA" : "#E5E5E5"}` }}>
              <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: forceSlot ? "#991B1B" : "#333" }}>Urgence — forcer l'atelier</span>
                <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#A1A1A1" }}>Ignore la capacité atelier. La disponibilité réception reste respectée.</span>
              </div>
              <Switch checked={forceSlot} onChange={(v) => { setForceSlot(v); setUrgentMode(v); }} />
            </div>
          )}

          {/* Why the button is disabled */}
          {!submitting && blockingReasons.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "8px 10px", borderRadius: 10, background: "#FFF7ED", border: "1px solid #FED7AA" }}>
              <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 11, color: "#9A3412" }}>Pour continuer :</span>
              {blockingReasons.map((reason) => (
                <span key={reason} style={{ fontFamily: "Poppins", fontSize: 12, color: "#C2410C" }}>• {reason}</span>
              ))}
            </div>
          )}

          <button
            onClick={handleNext}
            disabled={submitting || !canProceed}
            style={{
              fontFamily: "Poppins", fontWeight: 600, fontSize: 15,
              padding: "16px 20px", borderRadius: 12,
              background: submitting || !canProceed ? "#D9D9D9" : "#000",
              color: "#fff", border: "none",
              cursor: submitting || !canProceed ? "default" : "pointer",
              width: "100%",
            }}>
            {submitting ? "Envoi..." : step === STEP_COUNT - 1 ? "Confirmer la réservation" : "Continuer"}
          </button>
          <ProgressDots step={step} total={STEP_COUNT} />
        </div>
      )}

    </div>
  );
}
